import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Card, PageHeader, EmptyState, Tag, SkeletonRows } from '@/components/ui'
import { toast } from '@/components/ui/Toast'
import { useDataStore } from '@/stores/data'
import { bucketTasks } from '@/lib/taskList'
import { buildDeadlineFields, formatDeadline } from '@/lib/taskDeadline'
import { jstTodayStr } from '@/lib/date'
import type { Task } from '@/types/tasks'

const PRIORITIES: { value: Task['priority']; label: string }[] = [
  { value: 'high', label: '高' },
  { value: 'normal', label: '通常' },
  { value: 'low', label: '低' },
]

const PRIORITY_COLOR: Record<Task['priority'], string> = {
  high: 'var(--red)',
  normal: 'var(--text3)',
  low: 'var(--text3)',
}

export function Tasks() {
  const { tasks, fetchTasks, addTask, updateTask, deleteTask } = useDataStore()
  const loading = useDataStore((s) => s.loading.tasks)

  // --- Add form state ---
  const [title, setTitle] = useState('')
  const [date, setDate] = useState('') // optional YYYY-MM-DD
  const [time, setTime] = useState('') // optional HH:MM (requires date)
  const [priority, setPriority] = useState<Task['priority']>('normal')
  const [saving, setSaving] = useState(false)

  // --- Inline edit / view state ---
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const [showDone, setShowDone] = useState(false)

  useEffect(() => { fetchTasks() }, [fetchTasks])

  const todayStr = useMemo(() => jstTodayStr(), [])
  const { withDeadline, noDeadline, done } = useMemo(() => bucketTasks(tasks), [tasks])

  const onSubmit = useCallback(async () => {
    const t = title.trim()
    if (!t || saving) return
    setSaving(true)
    const { due_date, deadline_at } = buildDeadlineFields(date, time)
    const created = await addTask({ title: t, priority, due_date, deadline_at, source: 'tasks-page' })
    setSaving(false)
    if (created) {
      setTitle('')
      setDate('')
      setTime('')
      setPriority('normal')
      toast(due_date || deadline_at ? 'タスクを追加（期限あり）' : 'タスクを追加（期限なし）')
    } else {
      toast('保存に失敗しました')
    }
  }, [title, date, time, priority, saving, addTask])

  const toggleDone = useCallback(async (task: Task) => {
    const finishing = task.status !== 'done'
    await updateTask(task.id, {
      status: finishing ? 'done' : 'open',
      completed_at: finishing ? new Date().toISOString() : null,
    })
  }, [updateTask])

  const startEdit = (task: Task) => {
    setEditingId(task.id)
    setEditingTitle(task.title)
  }

  const commitEdit = async () => {
    if (!editingId) return
    const next = editingTitle.trim()
    const prev = tasks.find((t) => t.id === editingId)?.title ?? ''
    if (next && next !== prev) await updateTask(editingId, { title: next })
    setEditingId(null)
    setEditingTitle('')
  }

  const onDelete = useCallback(async (task: Task) => {
    const snippet = task.title.length > 24 ? task.title.slice(0, 24) + '…' : task.title
    if (!window.confirm(`削除しますか？\n\n「${snippet}」`)) return
    await deleteTask(task.id)
    toast('削除しました')
  }, [deleteTask])

  const renderRow = (task: Task) => {
    const badge = formatDeadline(task, todayStr)
    const isDone = task.status === 'done' || task.status === 'cancelled'
    return (
      <Card key={task.id} style={{ padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {/* complete checkbox */}
          <input
            type="checkbox"
            checked={isDone}
            onChange={() => toggleDone(task)}
            title={isDone ? '未完了に戻す' : '完了にする'}
            style={{ marginTop: 3, cursor: 'pointer', flexShrink: 0 }}
          />

          {/* title + meta */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingId === task.id ? (
              <input
                className="input"
                value={editingTitle}
                autoFocus
                onChange={(e) => setEditingTitle(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitEdit() }
                  else if (e.key === 'Escape') { setEditingId(null); setEditingTitle('') }
                }}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            ) : (
              <div
                onClick={() => !isDone && startEdit(task)}
                style={{
                  cursor: isDone ? 'default' : 'text',
                  wordBreak: 'break-word',
                  opacity: isDone ? 0.5 : 1,
                  textDecoration: isDone ? 'line-through' : 'none',
                }}
              >
                {task.title}
              </div>
            )}

            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {badge && (
                <span
                  style={{
                    fontSize: 11,
                    padding: '1px 7px',
                    borderRadius: 999,
                    color: badge.color,
                    background: badge.bg,
                    border: `1px solid ${badge.border}`,
                  }}
                >
                  {badge.label}
                </span>
              )}
              {task.priority !== 'normal' && (
                <span style={{ fontSize: 11, color: PRIORITY_COLOR[task.priority] }}>
                  {task.priority === 'high' ? '優先度: 高' : '優先度: 低'}
                </span>
              )}
              {task.companies?.name && <Tag>{task.companies.name}</Tag>}
            </div>
          </div>

          {/* delete */}
          <button
            className="btn btn-g btn-sm"
            onClick={() => onDelete(task)}
            title="削除"
            style={{ fontSize: 11, padding: '2px 6px', color: 'var(--danger, #dc2626)', flexShrink: 0 }}
          >
            削除
          </button>
        </div>
      </Card>
    )
  }

  const activeCount = withDeadline.length + noDeadline.length

  return (
    <div className="page">
      <PageHeader
        title="Tasks"
        description="期限のあるタスクも、いつかやることも、まずここに書き出す。スケジュール（カレンダー）とは別の、純粋なTODO置き場。"
      />

      {/* 入力 */}
      <Card style={{ marginBottom: 16 }}>
        <input
          className="input"
          placeholder="やること…（Enter で追加）"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onSubmit() }
          }}
          style={{ width: '100%', boxSizing: 'border-box', marginBottom: 8 }}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: 12, color: 'var(--text3)' }}>期限（任意）</label>
          <input
            className="input"
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value)
              if (!e.target.value) setTime('') // 日付を消したら時刻も消す
            }}
            style={{ fontSize: 13, padding: '2px 6px' }}
          />
          <input
            className="input"
            type="time"
            value={time}
            disabled={!date}
            onChange={(e) => setTime(e.target.value)}
            title={date ? '時刻（任意）' : '先に日付を選んでください'}
            style={{ fontSize: 13, padding: '2px 6px', opacity: date ? 1 : 0.5 }}
          />
          <select
            className="input"
            value={priority}
            onChange={(e) => setPriority(e.target.value as Task['priority'])}
            style={{ fontSize: 13, padding: '2px 6px' }}
          >
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>優先度: {p.label}</option>
            ))}
          </select>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-primary btn-sm"
            onClick={onSubmit}
            disabled={!title.trim() || saving}
          >
            {saving ? '保存中…' : '追加'}
          </button>
        </div>
      </Card>

      {/* 一覧 */}
      {loading && tasks.length === 0 ? (
        <SkeletonRows count={5} />
      ) : activeCount === 0 ? (
        <EmptyState
          icon="☑"
          message="未完了のタスクはありません。上の入力欄に書き出してみてください。"
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {withDeadline.length > 0 && (
            <section>
              <SectionLabel>期限あり（{withDeadline.length}）</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {withDeadline.map(renderRow)}
              </div>
            </section>
          )}
          {noDeadline.length > 0 && (
            <section>
              <SectionLabel>期限なし（{noDeadline.length}）</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {noDeadline.map(renderRow)}
              </div>
            </section>
          )}
        </div>
      )}

      {/* 完了済み */}
      {done.length > 0 && (
        <section style={{ marginTop: 20 }}>
          <button
            className="btn btn-g btn-sm"
            onClick={() => setShowDone((v) => !v)}
            style={{ fontSize: 12 }}
          >
            {showDone ? '▼' : '▶'} 完了済み（{done.length}）
          </button>
          {showDone && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {done.map(renderRow)}
            </div>
          )}
        </section>
      )}
    </div>
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '.08em',
        color: 'var(--text3)',
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  )
}
